import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, IconButton, Link as MuiLink, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "hooks/useAuth";
import useMfaStatus from "hooks/useMfaStatus";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { db } from "services/firebase";
import AuthPageShell from "components/auth/AuthPageShell";
import Loading from "components/Loading";
import AccountSetupFlow, { ACCOUNT_SETUP_STEPS } from "./AccountSetupFlow";
import MfaEnrollSteps from "./MfaEnrollSteps";
import ReauthDialog from "./ReauthDialog";
import { shouldSkipMfaEnrollmentUi } from "utils/isDevEnvironment";
import { roleRequiresMfa } from "utils/mfaPolicy";
import { isLearnerSignup } from "features/morningReport/learnerSignup";

/**
 * Onboarding page - For users who have Firebase Auth but need to complete profile setup
 *
 * Uses the shared AccountSetupFlow component for the actual form steps.
 * Supports ?role=patient|provider|facility param to pre-select account type.
 */
const Onboarding = () => {
  const { t } = useTranslation("account");
  const { user, userData, userLoading, logout } = useAuth();
  const { isEnrolled, sessionMfaLoading } = useMfaStatus();
  const [searchParams] = useSearchParams();
  const pendingInviteToken = sessionStorage.getItem(
    "pendingOrganizationInviteToken",
  );
  // Role the invitation is for (stored by JoinPage). AccountSetupFlow speaks
  // "provider" rather than "professional"; patient/admin pass through unchanged.
  const pendingInviteRole = sessionStorage.getItem(
    "pendingOrganizationInviteRole",
  );
  const invitedRoleForSetup =
    pendingInviteRole === "professional" ? "provider" : pendingInviteRole;
  // pendingSignupRole is stashed by the public /share landing page so a provider's
  // intended role survives the email-verification round-trip that strips ?role=.
  // localStorage (not sessionStorage): the email link opens a fresh tab.
  // Read once on mount: another tab that finishes setup first clears it, and
  // losing it mid-flow would drop this tab onto the account-type chooser.
  const [shareSignupRole] = useState(() => localStorage.getItem("pendingSignupRole"));
  // A MorningReport learner never sees the account-type chooser. AuthFlow already
  // wrote their profile at signup; this covers a profile that didn't land (e.g. an
  // OAuth signup, or a failed write). Read once on mount, like the hint above.
  const [learnerSignup] = useState(() =>
    isLearnerSignup({
      urlRole: searchParams.get("role"),
      hasInvite: Boolean(pendingInviteToken),
      hasShareCode:
        shareSignupRole === "provider" ||
        Boolean(localStorage.getItem("pendingShareReturn")),
    }),
  );
  const preselectedRole =
    searchParams.get("role") ||
    (pendingInviteToken ? invitedRoleForSetup || "provider" : null) ||
    (learnerSignup ? "learner" : null) ||
    shareSignupRole;
  // Providers arriving from /share already declared their role, so skip the
  // account-type chooser entirely (auto-create the profile) just like invites do.
  const fromShareSignup = Boolean(shareSignupRole) || learnerSignup;
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [step, setStep] = useState(ACCOUNT_SETUP_STEPS.ACCOUNT_TYPE);
  const [isOnSuccessStep, setIsOnSuccessStep] = useState(false);
  const [probeLoading, setProbeLoading] = useState(false);
  const [probedUserData, setProbedUserData] = useState(undefined);
  const [emailVerifiedRefreshLoading, setEmailVerifiedRefreshLoading] =
    useState(false);
  const [emailVerifiedOverride, setEmailVerifiedOverride] = useState(null);
  /** Blocks auto-redirect until AccountSetupFlow finishes commit (state so effects re-run when cleared). */
  const [signupInProgress, setSignupInProgress] = useState(false);
  const pendingReauthActionRef = useRef(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const navigate = useNavigate();
  const backHandlerRef = useRef(null);
  // `null` from the live listener can be stale: another tab (the signup tab polling
  // for verification, or the verify-link tab) may have just written the profile.
  // The server probe below settles it before the account-type chooser shows.
  const effectiveUserData = userData ?? probedUserData;
  const effectiveEmailVerified =
    emailVerifiedOverride === null
      ? !!user?.emailVerified
      : emailVerifiedOverride;
  const needsAccountSetup =
    !effectiveUserData?.role || effectiveUserData?.onboarding === true;
  // Risk-based: workforce roles must enroll MFA; patients are exempt (own-record
  // access only). Fail closed via roleRequiresMfa. Account setup runs first, so
  // the role is known by the time this gate matters.
  const needsMfaEnrollment =
    !!user &&
    !isEnrolled &&
    !shouldSkipMfaEnrollmentUi() &&
    roleRequiresMfa(effectiveUserData?.role);
  // Facility user who has filed a claim to join an existing facility and is
  // waiting for an admin to approve it. Held here (no dashboard) until then.
  const facilityClaimPending = effectiveUserData?.facilityClaimPending === true;
  const facilityClaimLocationName =
    effectiveUserData?.facilityClaimLocationName || "";
  // Facility claim was rejected by an admin. The user has role "facility" but
  // no facility membership, so the dashboard is an empty shell — hold them here
  // on a rejection screen instead.
  const facilityClaimRejected =
    effectiveUserData?.facilityClaimRejected === true;

  // Resolve the "undefined userData" ambiguity: still loading vs missing doc.
  useEffect(() => {
    let cancelled = false;
    const refreshEmailVerified = async () => {
      if (!user) {
        setEmailVerifiedOverride(null);
        return;
      }
      if (user.emailVerified) {
        setEmailVerifiedOverride(true);
        return;
      }
      setEmailVerifiedRefreshLoading(true);
      try {
        await firebase.auth().currentUser?.reload();
        if (cancelled) return;
        setEmailVerifiedOverride(!!firebase.auth().currentUser?.emailVerified);
      } catch (err) {
        if (cancelled) return;
        setEmailVerifiedOverride(!!user.emailVerified);
      } finally {
        if (!cancelled) setEmailVerifiedRefreshLoading(false);
      }
    };
    refreshEmailVerified();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Once a role is committed, drop the share-flow role hint so a later signup on
  // this device isn't pre-selected as a provider.
  useEffect(() => {
    if (effectiveUserData?.role) localStorage.removeItem("pendingSignupRole");
  }, [effectiveUserData?.role]);

  useEffect(() => {
    let cancelled = false;
    const probeUserDoc = async () => {
      if (!user || userData) {
        setProbeLoading(false);
        setProbedUserData(undefined);
        return;
      }
      setProbeLoading(true);
      try {
        // From the server, not the cache, which holds the same stale `missing`.
        const snap = await db
          .collection("users")
          .doc(user.uid)
          .get({ source: "server" });
        if (cancelled) return;
        setProbedUserData(snap.exists ? snap.data() : null);
      } catch (err) {
        if (cancelled) return;
        // Fail-open: avoid deadlock by allowing onboarding to continue.
        setProbedUserData(null);
      } finally {
        if (!cancelled) setProbeLoading(false);
      }
    };
    probeUserDoc();
    return () => {
      cancelled = true;
    };
  }, [user, userData]);

  // Check auth state and redirect if needed
  useEffect(() => {
    if (
      userLoading ||
      sessionMfaLoading ||
      probeLoading ||
      emailVerifiedRefreshLoading
    )
      return;

    if (user) {
      // Invitees haven't proved email ownership yet at the Auth layer — clicking
      // the invite link is the proof. acceptInvitation marks emailVerified=true
      // server-side once the user doc lands. Skip the bounce so the invitation
      // flow can complete instead of getting kicked back to /auth.
      if (!effectiveEmailVerified && !pendingInviteToken) {
        navigate("/auth");
        return;
      }
      if (
        !needsAccountSetup &&
        !needsMfaEnrollment &&
        !signupInProgress &&
        !facilityClaimPending &&
        !facilityClaimRejected
      ) {
        navigate("/dashboard");
        return;
      }
      setLoading(false);
    } else {
      // No authenticated user - redirect to auth. Just verified on a device with no
      // session: keep verified=1 (and the role) so /auth asks them to sign in.
      const verified = searchParams.get("verified") === "1";
      const role = searchParams.get("role");
      navigate(
        verified
          ? `/auth?verified=1${role ? `&role=${encodeURIComponent(role)}` : ""}`
          : "/auth",
      );
    }
  }, [
    navigate,
    searchParams,
    needsAccountSetup,
    needsMfaEnrollment,
    signupInProgress,
    facilityClaimPending,
    facilityClaimRejected,
    sessionMfaLoading,
    probeLoading,
    emailVerifiedRefreshLoading,
    effectiveEmailVerified,
    user,
    userData,
    userLoading,
  ]);

  const handleComplete = async () => {
    if (!pendingInviteToken) return;
    const acceptInvitation = firebase
      .functions()
      .httpsCallable("acceptInvitation");
    try {
      await acceptInvitation({ token: pendingInviteToken });
      sessionStorage.removeItem("pendingOrganizationInviteToken");
      sessionStorage.removeItem("pendingOrganizationInviteRole");
      // acceptInvitation flips emailVerified=true server-side. Reload the
      // local Auth user so DashboardPage's verification gate sees the change.
      try {
        await firebase.auth().currentUser?.reload();
        setEmailVerifiedOverride(
          !!firebase.auth().currentUser?.emailVerified,
        );
      } catch {
        /* ignore — DashboardPage has a fallback "resend verification" screen */
      }
    } catch (err) {
      const code = err?.code || "";
      const message = (err?.message || "").toLowerCase();
      const isTerminal =
        code.includes("already-exists") ||
        code.includes("permission-denied") ||
        code.includes("not-found") ||
        (code.includes("failed-precondition") &&
          (message.includes("accepted") ||
            message.includes("revoked") ||
            message.includes("expired") ||
            message.includes("different email") ||
            message.includes("organization")));
      if (isTerminal) {
        sessionStorage.removeItem("pendingOrganizationInviteToken");
        sessionStorage.removeItem("pendingOrganizationInviteRole");
        return;
      }
      throw err;
    }
  };

  const handleBack = () => {
    backHandlerRef.current?.();
  };

  if (loading || probeLoading || emailVerifiedRefreshLoading)
    return <Loading page />;

  // Facility access request awaiting admin approval — held here (no dashboard)
  // until an admin approves the claim, since the user has no facility access yet.
  if (facilityClaimPending) {
    return (
      <AuthPageShell glow={false}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h3" sx={{ mb: 1 }}>
            {t("onboarding.pendingTitle")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {facilityClaimLocationName
              ? t("onboarding.pendingNamed", { facility: facilityClaimLocationName })
              : t("onboarding.pendingGeneric")}{" "}
            {t("onboarding.pendingAfter")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            <Trans
              ns="account"
              i18nKey="onboarding.needSooner"
              components={{ 1: <MuiLink href="mailto:support@hlthdsk.com" /> }}
            />
          </Typography>
          <MuiLink
            component="button"
            variant="body2"
            onClick={async () => {
              try {
                await logout();
                navigate("/");
              } catch (err) {
                console.error("Logout error:", err);
              }
            }}
          >
            {t("common.signOut")}
          </MuiLink>
        </Box>
      </AuthPageShell>
    );
  }

  // Facility access request was rejected by an admin. The user has no facility
  // membership, so there is no dashboard for them — show why and how to follow
  // up instead of dropping them onto an empty shell.
  if (facilityClaimRejected) {
    return (
      <AuthPageShell glow={false}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h3" sx={{ mb: 1 }}>
            {t("onboarding.rejectedTitle")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            <Trans
              ns="account"
              i18nKey="onboarding.rejectedBody"
              components={{ 1: <MuiLink href="mailto:support@hlthdsk.com" /> }}
            />
          </Typography>
          <MuiLink
            component="button"
            variant="body2"
            onClick={async () => {
              try {
                await logout();
                navigate("/");
              } catch (err) {
                console.error("Logout error:", err);
              }
            }}
          >
            {t("common.signOut")}
          </MuiLink>
        </Box>
      </AuthPageShell>
    );
  }

  // Account setup done and MFA not required — brief state before navigate to dashboard
  if (!needsAccountSetup && !needsMfaEnrollment) {
    return <Loading page />;
  }

  return (
    <AuthPageShell glow={false} logoLinked={!isOnSuccessStep}>
      <Box>
        {/* Back button - only during account setup flow, and only when there is
            somewhere to go back to. An invited/preselected facility opens
            directly on the facility picker, so back would do nothing there. */}
        {needsAccountSetup &&
          step !== ACCOUNT_SETUP_STEPS.ACCOUNT_TYPE &&
          !(preselectedRole && step === ACCOUNT_SETUP_STEPS.FACILITY_INFO) &&
          !isOnSuccessStep && (
            <Box sx={{ mb: 2 }}>
              <IconButton onClick={handleBack} edge="start" sx={{ ml: -1 }}>
                <ArrowBack />
              </IconButton>
            </Box>
          )}

        {needsAccountSetup ? (
          <AccountSetupFlow
            onComplete={handleComplete}
            onStepChange={setStep}
            backHandlerRef={backHandlerRef}
            preselectedRole={preselectedRole}
            autoCreateProfile={Boolean(pendingInviteToken) || fromShareSignup}
            isInvitationFlow={Boolean(pendingInviteToken)}
            onSignupStart={() => setSignupInProgress(true)}
            onSignupEnd={() => setSignupInProgress(false)}
            onSuccessStep={setIsOnSuccessStep}
          />
        ) : (
          <MfaEnrollSteps
            subtitle={t("onboarding.mfaSubtitle")}
            onDone={() => navigate("/dashboard")}
            onRequireReauth={(retryFn) => {
              pendingReauthActionRef.current = retryFn;
              setReauthOpen(true);
            }}
          />
        )}

        {/* Footer with logout - hide on success step */}
        {!isOnSuccessStep && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ mt: 4, textAlign: "center", display: "block" }}
          >
            <MuiLink
              component="button"
              variant="caption"
              color="text.disabled"
              underline="hover"
              onClick={async () => {
                setLogoutLoading(true);
                try {
                  await logout();
                  navigate("/");
                } catch (err) {
                  console.error("Logout error:", err);
                  setLogoutLoading(false);
                }
              }}
              disabled={logoutLoading}
              sx={{ verticalAlign: "baseline" }}
            >
              {logoutLoading ? t("common.loggingOut") : t("common.logOut")}
            </MuiLink>
          </Typography>
        )}
      </Box>
      <ReauthDialog
        open={reauthOpen}
        onClose={() => setReauthOpen(false)}
        onSuccess={async () => {
          const action = pendingReauthActionRef.current;
          pendingReauthActionRef.current = null;
          setReauthOpen(false);
          if (action) await action();
        }}
      />
    </AuthPageShell>
  );
};

export default Onboarding;
